import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";
import { Reveal } from "./Reveal";
import { Chapter } from "./Chapter";

const initialForm = {
  name: "",
  email: "",
  position: "",
  production: "",
  projectType: "",
  union: "",
  message: "",
  website: "",
};

const TYPEWRITER_IMAGE =
  "https://images.unsplash.com/photo-1782758895890-117efb8ae623?auto=format&fit=crop&w=1800&q=80";

const SAGE_FILTER = "sepia(0.2) hue-rotate(30deg) saturate(0.75) brightness(1.03)";

// Red bottom border applied when a required field is left empty on submit
const invalidCls = "!border-b-red-700 !border-b-4 focus:!border-b-red-700";

export const Contact = () => {
  const { contact, chapters } = useContent();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [scriptFile, setScriptFile] = useState(null);
  const [scriptError, setScriptError] = useState("");
  const [fileKey, setFileKey] = useState(0);
  const [errors, setErrors] = useState({});

  // Default to the historical required set when the CMS hasn't stored an override yet
  const defaultRequired = { name: true, email: true, message: true };
  const required = { ...defaultRequired, ...(contact.requiredFields || {}) };
  const unionOptions = contact.unionOptions || ["Union", "Non-Union"];
  const isRequired = (key) => !!required[key];
  const errCls = (key) => (errors[key] ? invalidCls : "");
  const labelErrCls = (key) => (errors[key] ? "!text-red-700" : "");
  const reqStar = (key) => (isRequired(key) ? <span className="text-red-700"> *</span> : null);

  const update = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key] && value.trim()) setErrors((prev) => ({ ...prev, [key]: false }));
  };

  const validate = () => {
    const next = {};
    if (isRequired("name") && !form.name.trim()) next.name = true;
    if (isRequired("email") && !form.email.trim()) next.email = true;
    if (isRequired("position") && !form.position.trim()) next.position = true;
    if (isRequired("production") && !form.production.trim()) next.production = true;
    if (isRequired("projectType") && !form.projectType.trim()) next.projectType = true;
    if (isRequired("union") && !form.union.trim()) next.union = true;
    if (isRequired("message") && !form.message.trim()) next.message = true;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(contact.validationMessage || "Please complete the highlighted fields.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("position", form.position);
      fd.append("production", form.production);
      fd.append("project_type", form.projectType);
      fd.append("union", form.union);
      fd.append("message", form.message);
      fd.append("website", form.website);
      if (scriptFile) fd.append("script", scriptFile);
      await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(contact.successMessage);
      setForm(initialForm);
      setScriptFile(null);
      setScriptError("");
      setErrors({});
      setFileKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative overflow-hidden" data-testid="contact-section">
      <div aria-hidden="true" className="absolute inset-0">
        <img src={TYPEWRITER_IMAGE} alt="" className="h-full w-full object-cover opacity-[0.3]" style={{ filter: SAGE_FILTER }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(140,160,128,0.12)" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(247,243,238,0.68)" }} />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 md:px-12 py-32 md:py-44">
      <Chapter number="06" kicker={chapters.contact.kicker} title={chapters.contact.title} id="contact" />
      <div className="mt-20 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-serif font-normal leading-snug text-charcoal/75 max-w-md" style={{ fontSize: contact.fontSizes?.lead }} data-testid="contact-lead">
              {contact.lead}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4 text-charcoal/50" style={{ fontSize: contact.fontSizes?.lines }}>
              {contact.lines.map((line, i) => (
                <p key={i} className="flex items-center gap-3" data-testid={`contact-line-${i}`}>
                  <span className="h-px w-8 bg-champagne" /> {line}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} noValidate className="space-y-10" data-testid="contact-form">
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={update("website")}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="contact-honeypot-input"
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
              />
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-name" className={`form-label ${labelErrCls("name")}`} style={{ fontSize: contact.fontSizes?.label }}>
                    {contact.labels.name}{reqStar("name")}
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder={contact.placeholders.name}
                    className={`editorial-input mt-2 ${errCls("name")}`}
                    style={{ fontSize: contact.fontSizes?.input }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className={`form-label ${labelErrCls("email")}`} style={{ fontSize: contact.fontSizes?.label }}>
                    {contact.labels.email}{reqStar("email")}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={update("email")}
                    placeholder={contact.placeholders.email}
                    className={`editorial-input mt-2 ${errCls("email")}`}
                    style={{ fontSize: contact.fontSizes?.input }}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <label htmlFor="contact-position" className={`form-label ${labelErrCls("position")}`} style={{ fontSize: contact.fontSizes?.label }}>
                    {contact.labels.position}{reqStar("position")}
                  </label>
                  <input
                    id="contact-position"
                    data-testid="contact-position-input"
                    value={form.position}
                    onChange={update("position")}
                    placeholder={contact.placeholders.position}
                    className={`editorial-input mt-2 ${errCls("position")}`}
                    style={{ fontSize: contact.fontSizes?.input }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-production" className={`form-label ${labelErrCls("production")}`} style={{ fontSize: contact.fontSizes?.label }}>
                    {contact.labels.production}{reqStar("production")}
                  </label>
                  <input
                    id="contact-production"
                    data-testid="contact-production-input"
                    value={form.production}
                    onChange={update("production")}
                    placeholder={contact.placeholders.production}
                    className={`editorial-input mt-2 ${errCls("production")}`}
                    style={{ fontSize: contact.fontSizes?.input }}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-10 items-end">
                <div>
                  <label htmlFor="contact-projecttype" className={`form-label ${labelErrCls("projectType")}`} style={{ fontSize: contact.fontSizes?.label }}>
                    {contact.labels.projectType}{reqStar("projectType")}
                  </label>
                  <select
                    id="contact-projecttype"
                    data-testid="contact-projecttype-select"
                    value={form.projectType}
                    onChange={update("projectType")}
                    className={`editorial-input mt-2 cursor-pointer ${errCls("projectType")}`}
                    style={{ fontSize: contact.fontSizes?.input }}
                  >
                    <option value="">{contact.placeholders.projectType}</option>
                    {(contact.projectTypes || []).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div data-testid="contact-union-group">
                  <label className={`form-label ${labelErrCls("union")}`} style={{ fontSize: contact.fontSizes?.label }}>
                    {contact.labels.union || "Union Registration"}{reqStar("union")}
                  </label>
                  <div
                    className={`mt-4 flex flex-wrap items-center gap-6 ${errors.union ? "border-2 border-red-700 rounded px-3 py-2" : ""}`}
                  >
                    {unionOptions.map((opt) => {
                      const checked = form.union === opt;
                      return (
                        <label
                          key={opt}
                          className="inline-flex items-center gap-2 cursor-pointer select-none text-charcoal"
                          style={{ fontSize: contact.fontSizes?.input }}
                          data-testid={`contact-union-label-${opt.toLowerCase()}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((f) => ({ ...f, union: checked ? "" : opt }));
                              if (errors.union) setErrors((p) => ({ ...p, union: false }));
                            }}
                            data-testid={`contact-union-${opt.toLowerCase()}`}
                            className="h-5 w-5 accent-[#5A2633]"
                          />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="contact-script" className="form-label" style={{ fontSize: contact.fontSizes?.label }}>{contact.labels.script}</label>
                <input
                  key={fileKey}
                  id="contact-script"
                  type="file"
                  accept=".pdf,application/pdf"
                  data-testid="contact-script-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f && !(f.name.toLowerCase().endsWith(".pdf") && (f.type === "application/pdf" || f.type === ""))) {
                      setScriptError("Only PDF files can be uploaded. Please choose a .pdf file.");
                      setScriptFile(null);
                      e.target.value = "";
                      return;
                    }
                    setScriptError("");
                    setScriptFile(f);
                  }}
                  className="mt-3 block w-full text-sm text-charcoal/70 file:mr-4 file:rounded-full file:border file:border-burgundy file:bg-transparent file:px-5 file:py-2 file:text-[11px] file:font-semibold file:uppercase file:tracking-[0.25em] file:text-burgundy hover:file:bg-burgundy hover:file:text-ivory file:transition-colors file:cursor-pointer"
                />
                {scriptError && (
                  <p className="mt-2 text-sm font-semibold text-red-700" data-testid="contact-script-error">{scriptError}</p>
                )}
                {scriptFile && !scriptError && (
                  <p className="mt-2 text-xs text-charcoal/50" data-testid="contact-script-name">{scriptFile.name}</p>
                )}
              </div>
              <div>
                <label htmlFor="contact-message" className={`form-label ${labelErrCls("message")}`} style={{ fontSize: contact.fontSizes?.label }}>
                  {contact.labels.message}{reqStar("message")}
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={contact.placeholders.message}
                  className={`editorial-input mt-2 resize-none ${errCls("message")}`}
                  style={{ fontSize: contact.fontSizes?.input }}
                />
              </div>
              {Object.keys(errors).length > 0 && (
                <p className="text-sm font-semibold text-red-700" data-testid="contact-validation-message">
                  {contact.validationMessage || "Please complete the highlighted fields."}
                </p>
              )}
              <motion.button
                type="submit"
                disabled={sending}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                data-testid="contact-submit-button"
                className="inline-flex items-center rounded-full bg-burgundy px-14 py-6 text-sm font-semibold uppercase tracking-[0.3em] text-ivory transition-colors duration-300 hover:bg-charcoal disabled:opacity-60"
              >
                {sending ? contact.sendingLabel : contact.submitLabel}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
      </div>
    </section>
  );
};
